/**
 * @file そだちの土台カード (計画タブ)。
 *
 * @what: 伸びやすい時期のある土台 6 件を、目安・一言・打ち手 (タブや本だなへの導線)・
 *   根拠つきで一覧にする。チェックや達成の記録は持たない。
 * @why: 「いま何に触れさせるか」を選ぶ判断材料であって、成果指標ではない (測らない原則。
 *   代表 2026-08-24 草案承認)。成果は日々の記録と日記の気づきに現れる。
 * @failure: 描画のみ。sources が null (読込前・読込失敗) の間は出典リンクを出さず、文言だけで
 *   成立させる (スケルトンは出さない)。カタログの sourceId が本だなの実 id と食い違うと
 *   リンクが黙って消える形の退行になるので、テストで実 id を固定している。
 */

import { memo } from "react";
import { Link } from "react-router-dom";
import type { StudySource } from "../api/sources";
import { SODACHI_CATALOG, type SodachiAction, type SodachiTabView } from "../model/sodachiCatalog";
import "./study.css";

/** 中身は静的カタログなので、親 (計画タブ) の draft 変更ごとに再描画しない。 */
export const SodachiCard = memo(function SodachiCard({
  sources,
  sourceHrefBase,
  onOpenView,
}: {
  /** 本だなの資料 (出典リンクの出し分けに使う。未読込は null)。 */
  sources: readonly StudySource[] | null;
  sourceHrefBase: string;
  /** タブ切り替え (打ち手の導線)。 */
  onOpenView: (view: SodachiTabView) => void;
}) {
  const hasSource = (id: string) => sources?.some((s) => s.id === id) === true;
  // タブは「打ち手」の行、本だなのページは「根拠」の行に出す (同じ本の名前が二度並ばないように)
  const renderTab = (a: Extract<SodachiAction, { kind: "tab" }>) => (
    <button
      key={`tab-${a.view}-${a.label}`}
      type="button"
      className="study-link-btn"
      onClick={() => onOpenView(a.view)}
    >
      {a.label}
    </button>
  );
  const renderSource = (a: Extract<SodachiAction, { kind: "source" }>) => {
    if (!hasSource(a.sourceId)) return null;
    const query = a.page !== undefined ? `?page=${a.page}` : "";
    return (
      <Link
        key={`source-${a.sourceId}-${a.page ?? 0}`}
        to={`${sourceHrefBase}/${encodeURIComponent(a.sourceId)}${query}`}
      >
        {a.label}
      </Link>
    );
  };

  return (
    <section className="study-section">
      <h2 className="study-section-title">
        <span aria-hidden="true">🌱</span> そだちの土台
      </h2>
      <p className="study-section-help">
        伸びやすい時期のある土台と、わが家の打ち手です。時期は目安で、できた・できないは測りません。
      </p>
      <ul className="study-sodachi-list">
        {SODACHI_CATALOG.map((e) => (
          <li key={e.id} className="study-sodachi-item">
            <div className="study-sodachi-head">
              <strong>{e.label}</strong>
              <span className="study-sodachi-timing">{e.timing}</span>
            </div>
            <p className="study-sodachi-note">{e.note}</p>
            <div className="study-sodachi-actions">
              {e.actions.filter((a) => a.kind === "tab").map(renderTab)}
            </div>
            <p className="study-sodachi-evidence">
              {e.evidence}
              {e.actions.filter((a) => a.kind === "source").map((a) => (
                <span key={`ev-${a.sourceId}`}> ・{renderSource(a)}</span>
              ))}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
});
